import { useRef, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight, ChevronLeft, CheckCircle2, Phone, Compass, Settings, Layers, Rocket, BarChart3 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

/* ────────────────────────────────────────────
  REUSABLE ANIMATED SECTION WRAPPER
  ──────────────────────────────────────────── */
function Section({ children, className = '', id, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
  <section ref={ref} id={id} data-testid={testId} className={className}>
  <motion.div
  initial={{ opacity: 0, y: 30 }}
  animate={inView ? { opacity: 1, y: 0 } : {}}
  transition={{ duration: 0.5 }}
  >
  {children}
  </motion.div>
  </section>
  );
}

/* ────────────────────────────────────────────
  PROCESS - SCROLL-DRIVEN SEQUENTIAL FADE
  ──────────────────────────────────────────── */
const PROCESS_ICONS = [Compass, Settings, Layers, Rocket, BarChart3];

/* Evolving ring illustration - grows more complex per step */
function ProcessIllustration({ index }) {
  const rings = index + 1;
  return (
  <div className="relative w-[420px] h-[420px] mx-auto">
  <svg viewBox="0 0 420 420" className="w-full h-full">
  {/* Animated expanding rings */}
  {Array.from({ length: rings }).map((_, r) => {
  const rr = 40 + r * 32;
  const dashLen = 8 + r * 5;
  const strokeOp = 0.08 + r * 0.04;
  return (
  <motion.circle
  key={r}
  cx="210" cy="210" r={rr}
  fill="none"
  stroke={`rgba(245,132,31,${strokeOp})`}
  strokeWidth={r === rings - 1 ? 2 : 1.2}
  strokeDasharray={`${dashLen} ${dashLen * 0.8}`}
  initial={{ pathLength: 0, opacity: 0 }}
  animate={{ pathLength: 1, opacity: 1, rotate: r % 2 === 0 ? 360 : -360 }}
  transition={{ pathLength: { duration: 1, delay: r * 0.15, ease: 'easeOut' }, opacity: { duration: 0.5, delay: r * 0.15 }, rotate: { duration: 20 + r * 8, repeat: Infinity, ease: 'linear' } }}
  style={{ transformOrigin: '210px 210px' }}
  />
  );
  })}

  {/* Connecting lines from center to nodes */}
  {Array.from({ length: rings + 2 }).map((_, n) => {
  const angle = (n / (rings + 2)) * Math.PI * 2 - Math.PI / 2;
  const outerR = 40 + (rings - 1) * 32 + 20;
  const x2 = 210 + Math.cos(angle) * outerR;
  const y2 = 210 + Math.sin(angle) * outerR;
  const x1 = 210 + Math.cos(angle) * 50;
  const y1 = 210 + Math.sin(angle) * 50;
  return (
  <motion.line
  key={`line-${n}`}
  x1={x1} y1={y1} x2={x2} y2={y2}
  stroke="rgba(245,132,31,0.06)"
  strokeWidth={1}
  initial={{ pathLength: 0 }}
  animate={{ pathLength: 1 }}
  transition={{ duration: 0.6, delay: 0.5 + n * 0.06 }}
  />
  );
  })}

  {/* Outer nodes - pulsing dots */}
  {Array.from({ length: rings + 2 }).map((_, n) => {
  const angle = (n / (rings + 2)) * Math.PI * 2 - Math.PI / 2;
  const outerR = 40 + (rings - 1) * 32 + 20;
  const cx = 210 + Math.cos(angle) * outerR;
  const cy = 210 + Math.sin(angle) * outerR;
  return (
  <motion.circle
  key={`node-${n}`}
  cx={cx} cy={cy} r={4}
  fill="rgba(245,132,31,0.6)"
  initial={{ scale: 0, opacity: 0 }}
  animate={{ scale: [0, 1.3, 1], opacity: 1 }}
  transition={{ delay: 0.6 + n * 0.1, duration: 0.5 }}
  />
  );
  })}

  {/* Inner orbit dots - rotating */}
  {rings >= 2 && Array.from({ length: 3 }).map((_, n) => {
  const angle = (n / 3) * Math.PI * 2;
  const orbitR = 40 + 32;
  return (
  <motion.circle
  key={`orbit-${n}`}
  cx={210 + Math.cos(angle) * orbitR}
  cy={210 + Math.sin(angle) * orbitR}
  r={3}
  fill="rgba(245,132,31,0.8)"
  animate={{ rotate: 360 }}
  transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
  style={{ transformOrigin: '210px 210px' }}
  />
  );
  })}
  </svg>

  {/* Center icon with glow */}
  <div className="absolute inset-0 flex items-center justify-center">
  <motion.div
  initial={{ scale: 0.8, opacity: 0 }}
  animate={{ scale: 1, opacity: 1 }}
  transition={{ duration: 0.5, delay: 0.2 }}
  className="w-20 h-20 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shadow-[0_0_40px_rgba(245,132,31,0.1)]"
  >
  {PROCESS_ICONS[index] && (() => { const Icon = PROCESS_ICONS[index]; return <Icon size={36} className="text-orange-500" />; })()}
  </motion.div>
  </div>
  </div>
  );
}

function ProcessSlide({ step, index, total, scrollYProgress, isIntro }) {
  const slideStart = index / total;
  const slideEnd = (index + 1) / total;
  const progress = useTransform(scrollYProgress, [slideStart, slideEnd], [0, 1]);
  const opacity = useTransform(progress, [0, 0.2, 0.75, 1], [0, 1, 1, 0]);
  const y = useTransform(progress, [0, 0.2, 0.75, 1], [50, 0, 0, -50]);
  const scale = useTransform(progress, [0, 0.2, 0.75, 1], [0.96, 1, 1, 0.96]);

  if (isIntro) {
  return (
  <motion.div style={{ opacity, y, scale }} className="absolute inset-0 pt-[80px] pb-[70px] flex items-center justify-center px-6">
  <div className="max-w-4xl mx-auto w-full text-center">
  <p className="text-orange-500 text-xs font-semibold tracking-[0.25em] uppercase mb-6">OUR PROCESS</p>
  <h2 className="font-black text-navy-800 mb-6 leading-[0.92] tracking-tighter" style={{ fontSize: 'clamp(3rem, 2rem + 6vw, 6.5rem)' }}>
  How We{'\n'}Work
  </h2>
  <p className="text-navy-500 text-lg lg:text-xl max-w-xl mx-auto leading-relaxed">
  A proven, repeatable methodology refined over hundreds of engagements. Each phase builds on the last.
  </p>
  </div>
  </motion.div>
  );
  }

  const stepIndex = index - 1;
  return (
  <motion.div style={{ opacity, y, scale }} className="absolute inset-0 pt-[80px] pb-[70px] flex items-center justify-center px-6">
  <div className="max-w-5xl mx-auto w-full">
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
  {/* Left: Illustration */}
  <div className="hidden lg:flex items-center justify-center">
  <ProcessIllustration index={stepIndex} />
  </div>
  {/* Right: Text */}
  <div className="text-center lg:text-left">
  <p className="text-orange-500 text-xs font-semibold tracking-[0.25em] uppercase mb-5">
  {String(stepIndex + 1).padStart(2, '0')} - {step.phase.toUpperCase()}
  </p>
  <h2 className="font-black text-navy-800 mb-5 leading-[0.92] tracking-tighter" style={{ fontSize: 'clamp(2.5rem, 1.5rem + 4vw, 5rem)' }}>
  {step.phase}
  </h2>
  <p className="text-navy-500 text-lg max-w-lg leading-relaxed">
  {step.detail}
  </p>
  </div>
  </div>
  </div>
  </motion.div>
  );
}

function ProcessStageDot({ index, total, scrollYProgress }) {
  const dotOpacity = useTransform(scrollYProgress, [index / total, (index + 0.5) / total], [0.3, 1]);
  return <motion.div style={{ opacity: dotOpacity }} className="w-1.5 h-1.5 rounded-full bg-orange-500" />;
}

function ProcessSequential({ steps }) {
  const containerRef = useRef(null);
  const total = steps.length + 1; // +1 for intro slide
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end end'] });
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
  <section
  ref={containerRef}
  data-testid="subpage-process"
  className="relative bg-white"
  style={{ height: `${total * 100}vh`, zIndex: 0 }}
  >
  <div className="sticky top-0 h-screen overflow-hidden" style={{ zIndex: 1 }}>
  {/* Intro slide */}
  <ProcessSlide step={{}} index={0} total={total} scrollYProgress={scrollYProgress} isIntro />

  {/* Step slides */}
  {steps.map((step, i) => (
  <ProcessSlide key={i} step={step} index={i + 1} total={total} scrollYProgress={scrollYProgress} />
  ))}

  {/* Progress bar */}
  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-48">
  <div className="h-[2px] bg-navy-200 rounded-full overflow-hidden">
  <motion.div className="h-full bg-orange-500 rounded-full" style={{ width: progressWidth }} />
  </div>
  <div className="flex items-center justify-between mt-2.5">
  {Array.from({ length: total }).map((_, i) => (
  <ProcessStageDot key={i} index={i} total={total} scrollYProgress={scrollYProgress} />
  ))}
  </div>
  </div>

  {/* Scroll hint */}
  <motion.div
  className="absolute bottom-20 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
  style={{ opacity: useTransform(scrollYProgress, [0, 0.06], [1, 0]) }}
  >
  <span className="text-navy-400 text-xs uppercase tracking-widest">Scroll</span>
  <div className="w-px h-8 bg-gradient-to-b from-navy-300 to-transparent" />
  </motion.div>
  </div>
  </section>
  );
}

/* ────────────────────────────────────────────
  WHY FLYNAUT - ALL CARDS GRID
  ──────────────────────────────────────────── */
function WhyFlynautStacked({ items }) {
  return (
  <section data-testid="subpage-why" className="bg-navy-50 py-20 lg:py-28">
  <div className="max-w-content mx-auto px-6">
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-12">
  <p className="text-orange-500 font-semibold text-xs tracking-[0.2em] uppercase mb-3">Why Flynaut</p>
  <h2 className="text-navy-800 font-bold tracking-tight" style={{ fontSize: 'clamp(1.5rem, 1rem + 1.5vw, 2.25rem)', lineHeight: 1.15 }}>What Makes Us Different</h2>
  </motion.div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
  {items.map((item, i) => (
  <motion.div
  key={i}
  data-testid={`differentiator-${i}`}
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.4, delay: i * 0.1 }}
  className="group bg-white rounded-2xl border border-navy-100 p-7 lg:p-8 hover:shadow-lg hover:border-orange-200 transition-all duration-300"
  >
  <div className="flex items-center gap-4 mb-5">
  <span className="text-orange-500 font-black text-4xl" style={{ letterSpacing: '-0.04em', lineHeight: 1 }}>{String(i + 1).padStart(2, '0')}</span>
  <div className="h-px flex-1 bg-navy-100 group-hover:bg-orange-200 transition-colors" />
  </div>
  <h3 className="text-navy-800 font-bold text-lg mb-3 group-hover:text-navy-900">{item.title}</h3>
  <p className="text-navy-600 text-sm leading-relaxed">{item.desc}</p>
  </motion.div>
  ))}
  </div>
  </div>
  </section>
  );
}

/* ════════════════════════════════════════════
  MAIN TEMPLATE
  ════════════════════════════════════════════ */
export default function ServiceSubPage({ data }) {
  const {
  seo, hero, stats, challenge, approach,
  capabilities, process: steps, whyFlynaut,
  caseStudy, relatedServices, cta,
  parentLabel, parentUrl, url, sequentialProcess,
  } = data;

  const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
  { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://flynaut.com/' },
  { '@type': 'ListItem', position: 2, name: parentLabel, item: `https://flynaut.com${parentUrl}` },
  { '@type': 'ListItem', position: 3, name: seo.title.split('|')[0].trim() },
  ],
  };

  return (
  <div className="bg-white min-h-screen">
  <SEOHead
  title={seo.title}
  description={seo.description}
  canonical={url}
  schema={[breadcrumbSchema]}
  />
  <a href="#main-content" className="skip-nav">Skip to main content</a>
  <Navbar />
  <main id="main-content">

  {/* ─── 1. HERO + STATS ─── */}
  <section data-testid="subpage-hero" className="relative bg-[#1A202C] overflow-hidden" style={{ minHeight: '88vh' }}>
  {hero.video && (
  <>
  <video src={hero.video} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-40" poster={hero.image || undefined} />
  <div className="absolute inset-0 bg-gradient-to-r from-[#1A202C]/90 via-[#1A202C]/60 to-[#1A202C]/30" />
  <div className="absolute inset-0 bg-gradient-to-b from-[#1A202C]/30 via-transparent to-[#1A202C]/90" />
  </>
  )}
  {!hero.video && hero.image && (
  <>
  <img src={hero.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-40" style={hero.flipImage ? { transform: 'scaleX(-1)' } : undefined} />
  <div className="absolute inset-0 bg-gradient-to-r from-[#1A202C]/90 via-[#1A202C]/60 to-[#1A202C]/30" />
  <div className="absolute inset-0 bg-gradient-to-b from-[#1A202C]/30 via-transparent to-[#1A202C]/90" />
  </>
  )}
  {!hero.video && !hero.image && <div className="absolute inset-0 noise-subtle noise-dark" />}
  <div className="relative z-10 max-w-content mx-auto px-6 flex flex-col justify-center" style={{ minHeight: '88vh', paddingTop: '120px', paddingBottom: '48px' }}>
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
  {/* Breadcrumb */}
  <nav aria-label="Breadcrumb" data-testid="subpage-breadcrumb" className="mb-5">
  <ol className="flex items-center gap-1.5 text-sm">
  <li><Link to="/" className="text-white/40 hover:text-white/70 transition-colors">Home</Link></li>
  <li><ChevronRight size={12} className="text-white/25" /></li>
  <li><Link to={parentUrl} className="text-white/40 hover:text-white/70 transition-colors">{parentLabel}</Link></li>
  <li><ChevronRight size={12} className="text-white/25" /></li>
  <li className="text-white/70 truncate max-w-[260px]">{seo.title.split('|')[0].trim()}</li>
  </ol>
  </nav>

  <h1
  data-testid="subpage-h1"
  className="font-black tracking-tighter leading-[0.92] mb-6 max-w-3xl"
  style={{ fontSize: 'clamp(1.75rem, 1.25rem + 2.65vw, 3.8rem)' }}
  >
  {(() => {
  const text = hero.h1;
  // Split at internal period-space if exists
  const internalDot = text.indexOf('. ');
  if (internalDot > 0) {
  const firstPart = text.substring(0, internalDot + 1);
  const secondPart = text.substring(internalDot + 2);
  return (<><span className="text-white">{firstPart}</span><br /><span className="bg-gradient-to-r from-[#8B5CF6] via-[#06B6D4] to-[#F59E0B] bg-clip-text text-transparent">{secondPart}</span></>);
  }
  // Otherwise split at the midpoint word boundary
  const words = text.split(' ');
  const mid = Math.ceil(words.length / 2);
  const firstPart = words.slice(0, mid).join(' ');
  const secondPart = words.slice(mid).join(' ');
  return (<><span className="text-white">{firstPart}</span><br /><span className="bg-gradient-to-r from-[#8B5CF6] via-[#06B6D4] to-[#F59E0B] bg-clip-text text-transparent">{secondPart}</span></>);
  })()}
  </h1>
  <p className="text-[#A0AEC0] text-base lg:text-lg leading-relaxed max-w-2xl mb-10">
  {hero.subheadline}
  </p>
  <a
  href={cta.link}
  data-testid="subpage-hero-cta"
  className="group inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-7 py-3.5 text-sm font-medium transition-colors shadow-lg shadow-orange-500/20"
  >
  {cta.button} <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
  </motion.div>
  </div>
  </section>

  {/* Stats Bar */}
  <div data-testid="subpage-stats" className="bg-[#171923] border-t border-white/5">
  <div className="max-w-content mx-auto px-6 py-8 lg:py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
  {stats.map((s, i) => (
  <div key={i} className="text-center">
  <div className="text-orange-400 font-black text-3xl lg:text-4xl mb-1.5" style={{ letterSpacing: '-0.02em' }}>{s.label}</div>
  <div className="text-[#718096] text-xs lg:text-sm font-medium">{s.desc}</div>
  </div>
  ))}
  </div>
  </div>

  {/* ─── 2. THE CHALLENGE + 3. OUR APPROACH ─── */}
  <section data-testid="subpage-challenge" className="relative bg-[#0F1420] py-16 lg:py-24 overflow-hidden">
  {/* Subtle grid pattern */}
  <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
  <div className="relative z-10 max-w-content mx-auto px-6">
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
  {/* Challenge side */}
  <div>
  <div className="flex items-center gap-3 mb-6">
  <div className="w-8 h-[2px] bg-orange-500" />
  <p className="text-orange-400 font-semibold text-xs tracking-[0.2em] uppercase">The Challenge</p>
  </div>
  <h2 className="text-white font-bold tracking-tight mb-6" style={{ fontSize: 'clamp(1.5rem, 1rem + 1.5vw, 2.25rem)', lineHeight: 1.15 }}>
  {challenge.headline}
  </h2>
  <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 lg:p-8">
  <p className="text-[#A0AEC0] text-base leading-relaxed">{challenge.content}</p>
  </div>
  </div>
  {/* Approach side */}
  <div data-testid="subpage-approach">
  <div className="flex items-center gap-3 mb-6">
  <div className="w-8 h-[2px] bg-orange-500" />
  <p className="text-orange-400 font-semibold text-xs tracking-[0.2em] uppercase">Our Approach</p>
  </div>
  <div className="relative">
  <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-full bg-gradient-to-b from-orange-500 via-orange-400 to-transparent" />
  <div className="pl-7">
  <p className="text-[#CBD5E0] text-base leading-relaxed">{approach}</p>
  </div>
  </div>
  </div>
  </div>
  </div>
  </section>

  {/* ─── 4. CAPABILITIES ─── */}
  <section data-testid="subpage-capabilities" className="relative bg-[#0a0e1a] py-16 lg:py-24 overflow-hidden">
  {/* Radial glow accent */}
  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-orange-500/[0.04] rounded-full blur-[120px]" />
  <div className="relative z-10 max-w-content mx-auto px-6">
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-12">
  <div className="flex items-center gap-3 mb-4">
  <div className="w-8 h-[2px] bg-orange-500" />
  <p className="text-orange-400 font-semibold text-xs tracking-[0.2em] uppercase">What We Deliver</p>
  </div>
  <h2 className="text-white font-bold tracking-tight" style={{ fontSize: 'clamp(1.5rem, 1rem + 1.5vw, 2.25rem)', lineHeight: 1.15 }}>
  Capabilities
  </h2>
  </motion.div>
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
  {capabilities.map((cap, i) => (
  <motion.div
  key={i}
  data-testid={`capability-${i}`}
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.4, delay: i * 0.08 }}
  className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-6 lg:p-7 hover:bg-white/[0.06] hover:border-orange-500/20 transition-all duration-300"
  >
  {/* Top accent line on hover */}
  <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
  <div className="flex items-center gap-3 mb-4">
  <span className="text-orange-500/60 font-black text-2xl leading-none" style={{ letterSpacing: '-0.04em' }}>{String(i + 1).padStart(2, '0')}</span>
  <div className="h-px flex-1 bg-white/[0.06]" />
  </div>
  <h3 className="text-white font-bold text-base mb-3 group-hover:text-orange-100 transition-colors">{cap.title}</h3>
  <p className="text-[#718096] text-sm leading-relaxed">{cap.desc}</p>
  </motion.div>
  ))}
  </div>
  </div>
  </section>

  {/* ─── 5. OUR PROCESS ─── */}
  {sequentialProcess ? (
  <ProcessSequential steps={steps} />
  ) : (
  <section data-testid="subpage-process" className="bg-white py-20 lg:py-28">
  <div className="max-w-content mx-auto px-6">
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mb-12">
  <p className="text-orange-500 font-semibold text-xs tracking-[0.2em] uppercase mb-3">Our Process</p>
  <h2 className="text-navy-800 font-bold tracking-tight" style={{ fontSize: 'clamp(1.5rem, 1rem + 1.5vw, 2.25rem)', lineHeight: 1.15 }}>How We Work</h2>
  </motion.div>
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  {steps.map((step, i) => (
  <motion.div
  key={i}
  data-testid={`process-step-${i}`}
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.4, delay: i * 0.08 }}
  className="relative bg-navy-50 rounded-2xl p-7 group hover:bg-white hover:shadow-lg hover:border-orange-100 border border-transparent transition-all duration-300"
  >
  <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-t-2xl" />
  <span className="text-orange-500/40 font-black text-3xl block mb-4" style={{ letterSpacing: '-0.04em', lineHeight: 1 }}>{String(i + 1).padStart(2, '0')}</span>
  <h3 className="text-navy-800 font-bold text-base mb-2">{step.phase}</h3>
  <p className="text-navy-500 text-sm leading-relaxed">{step.detail}</p>
  </motion.div>
  ))}
  </div>
  </div>
  </section>
  )}

  {/* ─── 6. WHY FLYNAUT - Stacked Cards ─── */}
  <WhyFlynautStacked items={whyFlynaut} />

  {/* ─── 7. CASE STUDY - Results ─── */}
  <section data-testid="subpage-case-study" className="relative bg-[#0a0e1a] py-20 lg:py-28 overflow-hidden">
  <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-orange-500/[0.03] rounded-full blur-[120px]" />
  <div className="relative z-10 max-w-content mx-auto px-6">
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
  {/* Left: narrative */}
  <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
  <div className="flex items-center gap-3 mb-5">
  <div className="w-8 h-[2px] bg-orange-500" />
  <p className="text-orange-400 font-semibold text-xs tracking-[0.2em] uppercase">Results</p>
  </div>
  <h2 className="text-white font-bold tracking-tight mb-5" style={{ fontSize: 'clamp(1.5rem, 1rem + 1.5vw, 2.25rem)', lineHeight: 1.15 }}>
  {caseStudy.headline}
  </h2>
  <p className="text-[#A0AEC0] text-base leading-relaxed mb-6">{caseStudy.content}</p>
  <p className="text-white/30 text-xs italic">
  Results are illustrative, inspired by real client engagements. Specific metrics pending client verification.
  </p>
  </motion.div>
  {/* Right: metrics */}
  <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }}>
  <div className="space-y-4">
  {caseStudy.results.map((r, i) => (
  <motion.div
  key={i}
  initial={{ opacity: 0, y: 12 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.3, delay: 0.2 + i * 0.08 }}
  className="flex items-start gap-4 bg-white/[0.04] border border-white/[0.07] rounded-xl p-5 hover:bg-white/[0.06] transition-colors duration-300"
  >
  <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
  <CheckCircle2 size={16} className="text-orange-400" />
  </div>
  <span className="text-[#CBD5E0] text-sm leading-relaxed">{r}</span>
  </motion.div>
  ))}
  </div>
  </motion.div>
  </div>
  </div>
  </section>

  {/* ─── 8. RELATED SERVICES ─── */}
  <section data-testid="subpage-related" className="bg-[#0a0e1a] py-16 lg:py-20">
  <div className="max-w-content mx-auto px-6">
  <div className="flex items-center gap-3 mb-8">
  <div className="w-8 h-[2px] bg-orange-500" />
  <p className="text-orange-400 font-semibold text-xs tracking-[0.2em] uppercase">Related Services</p>
  </div>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
  {relatedServices.map((svc, i) => (
  <Link
  key={i}
  to={svc.url}
  data-testid={`related-service-${i}`}
  className="group bg-white/[0.04] border border-white/[0.08] hover:border-orange-500/30 hover:bg-white/[0.07] rounded-xl px-6 py-5 flex items-center justify-between transition-all duration-300"
  >
  <span className="text-white text-sm font-medium group-hover:text-orange-100">{svc.label}</span>
  <ArrowRight size={14} className="text-white/30 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all" />
  </Link>
  ))}
  </div>
  </div>
  </section>

  {/* ─── 9. CONVERSION CTA ─── */}
  <section data-testid="subpage-cta" className="bg-[#0d1117] py-24 lg:py-32">
  <div className="max-w-3xl mx-auto px-6 text-center">
  <motion.h2
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.6 }}
  className="text-white font-bold tracking-tight mb-5"
  style={{ fontSize: 'clamp(1.75rem, 1.25rem + 2vw, 2.75rem)', lineHeight: 1.12 }}
  >
  {cta.headline}
  </motion.h2>
  <motion.p
  initial={{ opacity: 0, y: 16 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.5, delay: 0.1 }}
  className="text-[#718096] text-base leading-relaxed max-w-xl mx-auto mb-10"
  >
  {cta.empathy}
  </motion.p>
  <motion.div
  initial={{ opacity: 0, y: 16 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.4, delay: 0.2 }}
  className="flex flex-col sm:flex-row items-center justify-center gap-5"
  >
  <a
  href={cta.link}
  data-testid="subpage-conversion-cta"
  className="group inline-flex items-center gap-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-9 py-4 text-base font-medium transition-all shadow-[0_0_32px_rgba(245,132,31,0.3)]"
  >
  {cta.button} <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
  <a
  href="tel:+1234567890"
  className="group inline-flex items-center gap-2.5 border border-white/15 text-white hover:border-white/30 hover:bg-white/5 rounded-full px-9 py-4 text-base font-medium transition-all"
  >
  <Phone size={16} /> Call Us
  </a>
  </motion.div>
  </div>
  </section>

  </main>
  <Footer />
  </div>
  );
}
